import { useEffect, useState, useMemo } from 'react';
import {
  BarChart2, ChevronDown, RefreshCw,
  CheckCircle2, Clock, AlertTriangle,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getCourseTaskResults } from '../../lib/tasks';
import type { TaskResultGroup } from '../../types/tasks';
import { Spinner } from '../ui/Spinner';
import { PageHeader } from '../ui/PageHeader';
import { fmtDateShort } from '../../lib/datetime';

function toNota5(score: number) {
  return (Math.round((1 + (score / 100) * 4) * 10) / 10).toFixed(1);
}

function heatColor(score: number): string {
  return `hsl(${Math.round(score * 1.2)} 80% 55%)`;
}

interface CourseOption { id: string; name: string }

export function TareasResultadosPage() {
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';

  const [courses, setCourses]           = useState<CourseOption[]>([]);
  const [selectedId, setSelectedId]     = useState('');
  const [groups, setGroups]             = useState<TaskResultGroup[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);

  useEffect(() => {
    async function load() {
      setLoadingCourses(true);
      let query = supabase.from('courses').select('id, name').eq('status', 'active').order('name');
      if (!isAdmin && profile?.id) {
        query = supabase
          .from('courses')
          .select('id, name')
          .or(`docente_id.eq.${profile.id},secondary_docente_id.eq.${profile.id}`)
          .eq('status', 'active')
          .order('name');
      }
      const { data } = await query;
      const list: CourseOption[] = data ?? [];
      setCourses(list);
      if (list.length > 0) setSelectedId(list[0].id);
      setLoadingCourses(false);
    }
    load();
  }, [isAdmin, profile?.id]);

  const loadResults = (courseId: string) => {
    if (!courseId) { setGroups([]); return; }
    let active = true;
    setLoadingResults(true);
    getCourseTaskResults(courseId)
      .then((g) => { if (active) setGroups(g); })
      .catch(() => {})
      .finally(() => { if (active) setLoadingResults(false); });
    return () => { active = false; };
  };

  useEffect(() => { loadResults(selectedId); }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    const slots = groups.flatMap((g) => g.students);
    const total = slots.length;
    const enviaron = slots.filter((s) => s.submission !== null).length;
    const scores = slots.filter((s) => s.submission !== null).map((s) => s.submission!.score_avg);
    const notaAvg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
    return { total, enviaron, pendientes: total - enviaron, notaAvg };
  }, [groups]);

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto h-full">
      <PageHeader
        eyebrow="Tareas"
        eyebrowIcon={BarChart2}
        title="Resultados de Tareas"
        description="Notas, intentos y estado por alumno, clase y curso."
        actions={
          <button
            onClick={() => loadResults(selectedId)}
            disabled={loadingResults}
            className="btn-glass"
          >
            <RefreshCw className={`w-4 h-4 ${loadingResults ? 'animate-spin' : ''}`} />
            Recargar
          </button>
        }
      />

      {/* Selector de curso */}
      <div className="glass-surface rounded-xl border border-white/[0.06] p-4">
        {loadingCourses ? (
          <Spinner />
        ) : courses.length === 0 ? (
          <p className="text-sm text-white/50">No hay cursos activos disponibles.</p>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-wider text-white/40 whitespace-nowrap">Curso</span>
            <div className="relative flex-1 max-w-sm">
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="w-full appearance-none bg-cubus-deep border border-white/10 rounded-lg px-3 py-2 pr-8 text-sm text-white focus:outline-none focus:border-cubus-lime/40"
              >
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
            </div>
          </div>
        )}
      </div>

      {/* Stats pills */}
      {!loadingResults && groups.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatPill label="Total alumnos" value={String(stats.total)} color="text-white" />
          <StatPill label="Enviaron" value={String(stats.enviaron)} color="text-cubus-lime" />
          <StatPill label="Pendientes" value={String(stats.pendientes)} color="text-amber-400" />
          <StatPill
            label="Nota promedio"
            value={stats.notaAvg !== null ? toNota5(stats.notaAvg) : '—'}
            style={stats.notaAvg !== null ? { color: heatColor(stats.notaAvg) } : undefined}
          />
        </div>
      )}

      {/* Contenido */}
      {loadingResults ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : groups.length === 0 && selectedId ? (
        <div className="text-center py-20 text-white/40">
          <BarChart2 className="w-10 h-10 mx-auto mb-3 text-white/20" />
          <p className="text-sm">Este curso aún no tiene tareas activas asignadas.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const submitted = group.students.filter((s) => s.submission !== null).length;
            const pct = group.students.length > 0
              ? Math.round((submitted / group.students.length) * 100)
              : 0;
            return (
              <div key={group.task_id} className="glass-surface rounded-2xl border border-white/[0.06] overflow-hidden">
                {/* Cabecera de la card */}
                <div className="flex items-center gap-4 px-5 py-3 border-b border-white/[0.06] bg-white/[0.02]">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-white/40 mb-0.5">
                      Clase {group.module_order + 1} · {group.module_name}
                    </p>
                    <p className="text-sm font-semibold text-white truncate">{group.task_title}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-mono text-white/50">{submitted}/{group.students.length}</span>
                    <div className="w-20 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: pct === 100 ? '#C8FF00' : pct >= 50 ? '#FBBF24' : '#F87171',
                        }}
                      />
                    </div>
                    <span
                      className="text-xs font-mono font-bold"
                      style={{ color: pct === 100 ? '#C8FF00' : pct >= 50 ? '#FBBF24' : '#F87171' }}
                    >
                      {pct}%
                    </span>
                  </div>
                </div>

                {/* Tabla de alumnos */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/[0.06] text-[10px] uppercase tracking-wider text-white/30">
                        <th className="text-left py-2.5 px-5">Alumno</th>
                        <th className="text-center py-2.5 px-4">Nota (1–5)</th>
                        <th className="text-center py-2.5 px-4">Puntaje</th>
                        <th className="text-center py-2.5 px-4">Intentos</th>
                        <th className="text-left   py-2.5 px-4">Último intento</th>
                        <th className="text-center py-2.5 px-4">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.students.map((s) => {
                        const sub = s.submission;
                        const avg = sub?.score_avg ?? 0;
                        const estado = !sub
                          ? 'pendiente'
                          : avg >= 80 ? 'logrado'
                          : avg >= 50 ? 'regular'
                          : 'por-mejorar';
                        return (
                          <tr
                            key={s.student_id}
                            className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02] transition-colors text-sm"
                          >
                            <td className="py-2.5 px-5">
                              <p className="text-white font-medium leading-tight">{s.student_name}</p>
                              <p className="text-[11px] text-white/40 leading-tight">{s.student_email}</p>
                            </td>
                            <td className="py-2.5 px-4 text-center">
                              {sub ? (
                                <span className="font-bold font-mono text-base" style={{ color: heatColor(avg) }}>
                                  {toNota5(avg)}
                                </span>
                              ) : <span className="text-white/25 text-base">—</span>}
                            </td>
                            <td className="py-2.5 px-4 text-center">
                              {sub ? (
                                <span className="font-mono text-sm text-white/70">{avg}%</span>
                              ) : <span className="text-white/25">—</span>}
                            </td>
                            <td className="py-2.5 px-4 text-center">
                              {sub ? (
                                <span className="font-mono text-sm text-white/70">{sub.attempt_count}</span>
                              ) : <span className="text-white/25">—</span>}
                            </td>
                            <td className="py-2.5 px-4 text-[11px] text-white/60">
                              {sub ? fmtDateShort(sub.submitted_at) : <span className="text-white/25">—</span>}
                            </td>
                            <td className="py-2.5 px-4 text-center">
                              <EstadoBadge estado={estado} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EstadoBadge({ estado }: { estado: string }) {
  if (estado === 'logrado')
    return <span className="chip chip-emerald inline-flex items-center gap-1"><CheckCircle2 size={10} /> Logrado</span>;
  if (estado === 'regular')
    return <span className="chip chip-amber inline-flex items-center gap-1"><AlertTriangle size={10} /> Regular</span>;
  if (estado === 'por-mejorar')
    return <span className="chip chip-rose inline-flex items-center gap-1"><AlertTriangle size={10} /> Por mejorar</span>;
  return <span className="chip chip-amber inline-flex items-center gap-1"><Clock size={10} /> Pendiente</span>;
}

function StatPill({
  label, value, color, style,
}: { label: string; value: string; color?: string; style?: React.CSSProperties }) {
  return (
    <div className="glass-surface rounded-xl border border-white/[0.06] px-4 py-3">
      <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</p>
      <p className={`text-display text-2xl font-bold ${color ?? ''}`} style={style}>{value}</p>
    </div>
  );
}
